import {
  ChevronDown,
  ChevronRight,
  Edit2,
  Loader2,
  Lock,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/AlertDialog.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Label } from "../../components/ui/Label.jsx";
import { useToast } from "../../hooks/useToast.js";
import {
  createIndustryTemplate,
  deleteIndustryTemplate,
  fetchIndustryTemplatesAdmin,
  updateIndustryTemplate,
} from "../../services/superAdminService.js";

export default function IndustryTemplates() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Add template
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");

  // Edit template label
  const [editingKey, setEditingKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  // Add field to template
  const [addingFieldTo, setAddingFieldTo] = useState(null);
  const [newFieldLabel, setNewFieldLabel] = useState("");

  // Edit field
  const [editingField, setEditingField] = useState(null); // { templateKey, fieldIdx }
  const [editFieldLabel, setEditFieldLabel] = useState("");

  // Add option to field
  const [addingOptionTo, setAddingOptionTo] = useState(null); // { templateKey, fieldIdx }
  const [newOptionLabel, setNewOptionLabel] = useState("");

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type, key, fieldIdx?, optionIdx?, label }

  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetchIndustryTemplatesAdmin();
      setTemplates(res.data || []);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ─── Save helper ──────────────────────────────────────

  const saveTemplate = async (key, label, fields) => {
    setSaving(true);
    try {
      await updateIndustryTemplate(key, { label, fields });
      toast({ title: "Template saved" });
      await load();
    } catch (err) {
      toast({ variant: "destructive", title: "Save failed", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const getTemplate = (key) => templates.find((t) => t.key === key);

  // ─── Template CRUD ────────────────────────────────────

  const handleAddTemplate = async () => {
    if (!newKey.trim() || !newLabel.trim()) return;
    setSaving(true);
    try {
      await createIndustryTemplate({
        key: newKey.trim().toLowerCase().replace(/\s+/g, "_"),
        label: newLabel.trim(),
        fields: [],
      });
      toast({ title: "Template created" });
      setShowAdd(false);
      setNewKey("");
      setNewLabel("");
      await load();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEditLabel = async (key) => {
    if (!editLabel.trim()) return;
    const t = getTemplate(key);
    await saveTemplate(key, editLabel.trim(), t.fields);
    setEditingKey(null);
    setEditLabel("");
  };

  const handleDeleteTemplate = async () => {
    if (!deleteConfirm || deleteConfirm.type !== "template") return;
    setSaving(true);
    try {
      await deleteIndustryTemplate(deleteConfirm.key);
      toast({ title: "Template deleted" });
      setDeleteConfirm(null);
      if (expanded === deleteConfirm.key) setExpanded(null);
      await load();
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  // ─── Field CRUD ───────────────────────────────────────

  const handleAddField = async (templateKey) => {
    if (!newFieldLabel.trim()) return;
    const t = getTemplate(templateKey);
    const updatedFields = [...t.fields, { label: newFieldLabel.trim(), options: [] }];
    await saveTemplate(templateKey, t.label, updatedFields);
    setAddingFieldTo(null);
    setNewFieldLabel("");
  };

  const handleEditField = async () => {
    if (!editingField || !editFieldLabel.trim()) return;
    const { templateKey, fieldIdx } = editingField;
    const t = getTemplate(templateKey);
    const updatedFields = [...t.fields];
    updatedFields[fieldIdx] = { ...updatedFields[fieldIdx], label: editFieldLabel.trim() };
    await saveTemplate(templateKey, t.label, updatedFields);
    setEditingField(null);
    setEditFieldLabel("");
  };

  const handleDeleteField = async () => {
    if (!deleteConfirm || deleteConfirm.type !== "field") return;
    const { key, fieldIdx } = deleteConfirm;
    const t = getTemplate(key);
    const updatedFields = t.fields.filter((_, i) => i !== fieldIdx);
    await saveTemplate(key, t.label, updatedFields);
    setDeleteConfirm(null);
  };

  // ─── Option CRUD ──────────────────────────────────────

  const handleAddOption = async () => {
    if (!addingOptionTo || !newOptionLabel.trim()) return;
    const { templateKey, fieldIdx } = addingOptionTo;
    const t = getTemplate(templateKey);
    const updatedFields = [...t.fields];
    updatedFields[fieldIdx] = {
      ...updatedFields[fieldIdx],
      options: [...updatedFields[fieldIdx].options, newOptionLabel.trim()],
    };
    await saveTemplate(templateKey, t.label, updatedFields);
    setAddingOptionTo(null);
    setNewOptionLabel("");
  };

  const handleToggleRequired = async (templateKey, fieldIdx) => {
    const t = getTemplate(templateKey);
    const updatedFields = [...t.fields];
    updatedFields[fieldIdx] = {
      ...updatedFields[fieldIdx],
      required: !updatedFields[fieldIdx].required,
    };
    await saveTemplate(templateKey, t.label, updatedFields);
  };

  const handleDeleteOption = async () => {
    if (!deleteConfirm || deleteConfirm.type !== "option") return;
    const { key, fieldIdx, optionIdx } = deleteConfirm;
    const t = getTemplate(key);
    const updatedFields = [...t.fields];
    updatedFields[fieldIdx] = {
      ...updatedFields[fieldIdx],
      options: updatedFields[fieldIdx].options.filter((_, i) => i !== optionIdx),
    };
    await saveTemplate(key, t.label, updatedFields);
    setDeleteConfirm(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "template") handleDeleteTemplate();
    else if (deleteConfirm.type === "field") handleDeleteField();
    else if (deleteConfirm.type === "option") handleDeleteOption();
  };

  // ─── Render ───────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Industry Templates</h1>
          <p className="text-xs text-gray-500 mt-1">
            These templates pre-configure fields when a new company signs up. Changes apply to new signups only.
          </p>
        </div>
        {!showAdd && (
          <Button
            onClick={() => setShowAdd(true)}
            className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
          >
            <Plus size={14} className="mr-1" />
            Add Template
          </Button>
        )}
      </div>

      {/* Add Template Form */}
      {showAdd && (
        <Card className="border-2 border-blue-100/50">
          <CardContent className="p-4 space-y-2">
            <Label className="text-xs font-semibold">New Industry Template</Label>
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={(e) => {
                  setNewLabel(e.target.value);
                  setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_"));
                }}
                placeholder="e.g. Automotive, Agriculture"
                className="h-8 text-xs flex-1"
                autoFocus
              />
              <Button
                onClick={handleAddTemplate}
                disabled={saving || !newLabel.trim()}
                className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : "Create"}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setShowAdd(false); setNewKey(""); setNewLabel(""); }}>
                <X size={14} />
              </Button>
            </div>
            {newKey && <p className="text-[10px] text-gray-400">Key: {newKey}</p>}
          </CardContent>
        </Card>
      )}

      {/* Templates List */}
      <div className="grid gap-3">
        {templates.map((t) => (
          <Card key={t.key} className="border-gray-100 overflow-hidden">
            <CardContent className="p-0">
              {/* Template Header */}
              <div className="flex items-center justify-between px-4 py-3">
                {editingKey === t.key ? (
                  <div className="flex gap-2 flex-1 mr-2">
                    <Input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      className="h-7 text-xs flex-1"
                      onKeyDown={(e) => e.key === "Enter" && handleEditLabel(t.key)}
                      autoFocus
                    />
                    <Button onClick={() => handleEditLabel(t.key)} disabled={saving} className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]">
                      Save
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingKey(null)}>
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setExpanded(expanded === t.key ? null : t.key)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {expanded === t.key ? (
                        <ChevronDown size={16} className="text-gray-400" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-400" />
                      )}
                      <span className="text-sm font-semibold text-gray-800">{t.label}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        {t.key}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {t.fields.length} fields
                      </span>
                    </button>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingKey(t.key); setEditLabel(t.label); }}>
                        <Edit2 size={13} className="text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm({ type: "template", key: t.key, label: t.label })}>
                        <Trash2 size={13} className="text-red-400" />
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Expanded: Fields & Options */}
              {expanded === t.key && (
                <div className="border-t border-gray-100 bg-gray-50/50 p-4 space-y-3">
                  {t.fields.map((field, fIdx) => (
                    <div key={fIdx} className="bg-white rounded-lg border border-gray-100 p-3">
                      {/* Field Header */}
                      <div className="flex items-center justify-between mb-2">
                        {editingField?.templateKey === t.key && editingField?.fieldIdx === fIdx ? (
                          <div className="flex gap-2 flex-1 mr-2">
                            <Input value={editFieldLabel} onChange={(e) => setEditFieldLabel(e.target.value)} className="h-6 text-xs flex-1" onKeyDown={(e) => e.key === "Enter" && handleEditField()} autoFocus />
                            <Button onClick={handleEditField} disabled={saving} className="h-6 px-2 text-[10px] text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]">Save</Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditingField(null)}><X size={10} /></Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-1.5 flex-1">
                              <p className="text-xs font-semibold text-gray-700">
                                {field.label}
                                <span className="text-gray-400 font-normal ml-1">({field.options.length})</span>
                              </p>
                              {field.required && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 flex items-center gap-0.5">
                                  <Lock size={7} /> Required
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1 items-center">
                              <button
                                onClick={() => handleToggleRequired(t.key, fIdx)}
                                className={`text-[9px] px-1.5 py-0.5 rounded border ${
                                  field.required
                                    ? "border-amber-200 text-amber-600 bg-amber-50"
                                    : "border-gray-200 text-gray-400"
                                }`}
                                title={field.required ? "Make optional" : "Make required (locked for new signups)"}
                              >
                                {field.required ? "Required" : "Optional"}
                              </button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingField({ templateKey: t.key, fieldIdx: fIdx }); setEditFieldLabel(field.label); }}>
                                <Edit2 size={11} className="text-gray-400" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setDeleteConfirm({ type: "field", key: t.key, fieldIdx: fIdx, label: field.label })}>
                                <Trash2 size={11} className="text-red-400" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Options */}
                      <div className="flex flex-wrap gap-1">
                        {field.options.map((opt, oIdx) => (
                          <span
                            key={oIdx}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600 flex items-center gap-1 group"
                          >
                            {opt}
                            <button
                              onClick={() => setDeleteConfirm({ type: "option", key: t.key, fieldIdx: fIdx, optionIdx: oIdx, label: opt })}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={8} className="text-red-400" />
                            </button>
                          </span>
                        ))}

                        {/* Add option inline */}
                        {addingOptionTo?.templateKey === t.key && addingOptionTo?.fieldIdx === fIdx ? (
                          <div className="flex gap-1 items-center">
                            <Input
                              value={newOptionLabel}
                              onChange={(e) => setNewOptionLabel(e.target.value)}
                              className="h-5 text-[10px] w-28 px-1"
                              placeholder="Option name"
                              onKeyDown={(e) => e.key === "Enter" && handleAddOption()}
                              autoFocus
                            />
                            <Button onClick={handleAddOption} disabled={saving || !newOptionLabel.trim()} className="h-5 px-1.5 text-[10px] text-white rounded bg-[#00a8b4]">Add</Button>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setAddingOptionTo(null)}><X size={8} /></Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setAddingOptionTo({ templateKey: t.key, fieldIdx: fIdx }); setNewOptionLabel(""); }}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400"
                          >
                            + Add
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add Field */}
                  {addingFieldTo === t.key ? (
                    <div className="flex gap-2">
                      <Input
                        value={newFieldLabel}
                        onChange={(e) => setNewFieldLabel(e.target.value)}
                        placeholder="Field name (e.g. Budget Range)"
                        className="h-7 text-xs flex-1"
                        onKeyDown={(e) => e.key === "Enter" && handleAddField(t.key)}
                        autoFocus
                      />
                      <Button onClick={() => handleAddField(t.key)} disabled={saving || !newFieldLabel.trim()} className="h-7 px-2 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]">Add</Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setAddingFieldTo(null); setNewFieldLabel(""); }}><X size={12} /></Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => { setAddingFieldTo(t.key); setNewFieldLabel(""); }}
                      className="h-7 px-3 text-xs rounded-full w-full border-dashed"
                    >
                      <Plus size={12} className="mr-1" />
                      Add Field
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <p className="text-center py-10 text-gray-400 text-sm">No templates found.</p>
        )}
      </div>

      {/* Delete Confirm Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteConfirm?.type === "template" ? "template" : deleteConfirm?.type === "field" ? "field" : "option"} "{deleteConfirm?.label}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === "template"
                ? "This will remove all fields and options from this template. New signups won't see it."
                : deleteConfirm?.type === "field"
                  ? "This will remove this field and all its options from the template."
                  : "This option will be removed from the template."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
